import React from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';
import { Button } from './button';

interface UnsavedChangesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void | Promise<void>;
  onDiscard: () => void;
  title?: string;
  description?: string;
  saveText?: string;
  discardText?: string;
  cancelText?: string;
}

/**
 * Dialog that prompts the user to save or discard unsaved changes
 * Used when closing tabs, popups, or navigating away from edited content
 */
export function UnsavedChangesDialog({
  isOpen,
  onClose,
  onSave,
  onDiscard,
  title = 'Unsaved Changes',
  description = 'You have unsaved changes. Would you like to save them before closing?',
  saveText = 'Save',
  discardText = 'Discard',
  cancelText = 'Cancel',
}: UnsavedChangesDialogProps) {
  const [isSaving, setIsSaving] = React.useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
          >
            {cancelText}
          </Button>
          <Button
            variant="destructive"
            onClick={onDiscard}
            disabled={isSaving}
          >
            {discardText}
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : saveText}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

